import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChartProps, ComparisonSelection } from './types'

import {
  Loading,
  MetricSelect,
  ScalrEmptyBox,
  ScalrSelect,
  SingleBranchSelect,
  TimeFilterButtons,
} from '@/components/customComponents'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import useMasterChart from '@/hooks/useMasterChart'

import { DEFAULT_CHART_OPTIONS } from '@/constants/CHART_CONSTANTS'
import { selectRegions, selectTenant, useAppSelector } from '@/store'
import { Interval } from '@/types/clarity/metrics/sales'
import { getFixedInterval, getMetricFilters } from '@/utils'
import { useTranslation } from 'react-i18next'

const getDefaultTypeFilter = (chartType: string) => {
  switch (chartType) {
    case 'sales':
      return 'adns'
    case 'labor':
      return 'laborRatio'
    case 'speedOfService':
      return 'speedOfService'
    case 'spend':
      return 'spend'
    case 'pnl':
      return 'salesAmount'
    default:
      return 'adns'
  }
}

export function MasterChart({
  chartType,
  dateRange,
  selectedBranchFilter,
  chartTitle,
}: ChartProps) {
  const { t } = useTranslation()
  const tenant = useAppSelector(selectTenant)
  const regions = useAppSelector(selectRegions)

  // Find the appropriate initial branch based on tenant and filters
  const getInitialBranch = useCallback(() => {
    if (!selectedBranchFilter) return undefined

    if (tenant === 'blankstreet') {
      for (const region of regions) {
        const initialBranch = region.branches.find((branch) =>
          branch.name.includes('Charles & Pinckney St')
        )
        if (initialBranch) {
          return initialBranch.name
        }
      }
      return 'Charles & Pinckney St'
    } else if (tenant === 'dev') {
      return 'Vista'
    }

    return ''
  }, [selectedBranchFilter, tenant, regions])

  const initialBranch = useMemo(() => getInitialBranch(), [getInitialBranch])

  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(
    initialBranch
  )
  const [selectedComparison, setSelectedComparison] =
    useState<ComparisonSelection | null>()
  const [typeFilter, setTypeFilter] = useState(getDefaultTypeFilter(chartType))
  const [intervalValue, setIntervalValue] = useState<Interval>(
    chartType === 'spend' || chartType === 'pnl' ? 'month' : 'day'
  )
  const [lineChartOptions, setLineChartOptions] = useState<EChartsOption>({
    ...DEFAULT_CHART_OPTIONS,
  })

  const memoizedSetLineChartOptions = useCallback((options: EChartsOption) => {
    setLineChartOptions(options)
  }, [])

  const memoizedSetIntervalValue = useCallback((interval: Interval) => {
    setIntervalValue(interval)
  }, [])

  useEffect(() => {
    if (selectedBranchFilter && regions.length > 0) {
      const newBranch = getInitialBranch()
      if (newBranch) {
        setSelectedBranch(newBranch)
      }
    }
  }, [selectedBranchFilter, tenant, regions.length, getInitialBranch])
  const { loading } = useMasterChart(
    dateRange,
    intervalValue,
    memoizedSetIntervalValue,
    selectedBranch,
    selectedComparison,
    typeFilter,
    DEFAULT_CHART_OPTIONS,
    memoizedSetLineChartOptions
  )

  function getChartTitle() {
    if (
      selectedComparison?.type === 'metric' ||
      selectedComparison?.type === 'period'
    ) {
      return `${chartTitle}`
    }
    return chartTitle
  }

  return (
    <Card>
      <CardHeader className="p-4">
        <div className="flex items-center justify-between">
          <CardTitle>{getChartTitle()}</CardTitle>
          <div className="flex items-center">
            {/* Branch Select */}
            <div className="mr-2">
              <SingleBranchSelect
                selectedBranch={selectedBranch}
                onSelectBranch={setSelectedBranch}
              />
            </div>
            {/* Metric Select */}
            <div className="mr-2">
              <MetricSelect
                onValueChange={setTypeFilter}
                defaultValue={typeFilter}
                filterTypes={getMetricFilters(chartType)}
              />
            </div>
            {/* Comparison Select */}
            <div className="mr-2">
              <ScalrSelect
                options={regions}
                onValueChange={setSelectedComparison}
                selectedBranch={selectedBranch}
                onBranchChange={setSelectedBranch}
                chartType={chartType}
              />
            </div>
            {/* Time Filter Buttons - TODO: could be a smaller component in a selection */}
            <TimeFilterButtons
              dateRange={dateRange}
              intervalValue={intervalValue}
              setIntervalValue={setIntervalValue}
              fixedInterval={getFixedInterval(typeFilter)}
              comparison={selectedComparison}
              chartType={chartType}
            />
            {/* TODO: Apply Filter Button? */}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 pt-0 pb-0">
        {loading ? (
          <Loading height={300} />
        ) : (
          (() => {
            const hasData = Array.isArray(lineChartOptions.series)
              ? lineChartOptions.series.some(
                  (s: any) =>
                    s.data && Array.isArray(s.data) && s.data.length > 0
                )
              : false

            return hasData ? (
              <ReactEcharts
                option={lineChartOptions}
                style={{ height: '60vh', width: '100%' }}
              />
            ) : (
              <ScalrEmptyBox
                title={t('emptyStates.thisChartIsEmpty')}
                message={t('emptyStates.noData')}
              />
            )
          })()
        )}
      </CardContent>
    </Card>
  )
}
